import { toCents, roundCents } from "../money.js";
import { parseWeight } from "../rules/dims.js";
import { NAMESPACE_HAULSHEET_LEGACY } from "../ids.js";
import { analyze } from "../pay/analyze.js";
import { CostRuleKind, FscRuleKind, PayContractKind } from "../enums.js";
import { uuidv5 } from "./uuidv5.js";
export { NAMESPACE_HAULSHEET_LEGACY };
/** Deterministic entity id from a legacy key. */
export function legacyEntityId(kind, legacyKey) {
    return uuidv5(`${kind}:${legacyKey}`, NAMESPACE_HAULSHEET_LEGACY);
}
/**
 * Map a legacy load to core cents fields.
 * fsc dollars total → fscCentsPerMile = toCents(fsc) / loadedMiles
 */
export function mapLegacyLoad(raw, legacyId) {
    const loadedMiles = Math.max(0, Math.round(raw.loadedMiles ?? 0));
    const fscDollars = raw.fsc ?? 0;
    const fscCentsPerMile = loadedMiles > 0 ? toCents(fscDollars) / loadedMiles : 0;
    let status = raw.taken
        ? "delivered"
        : "considering";
    if (!raw.taken &&
        typeof raw.heroWord === "string" &&
        raw.heroWord.toLowerCase() === "pass") {
        status = "passed";
    }
    const weightLb = raw.weight == null
        ? null
        : typeof raw.weight === "number"
            ? Math.round(raw.weight)
            : parseWeight(String(raw.weight));
    return {
        legacyId,
        status,
        source: "legacy_import",
        linehaulCents: toCents(raw.linehaul ?? 0),
        fscCentsPerMile,
        accessorialsCents: toCents(raw.accessorials ?? 0),
        loadedMiles,
        deadheadMiles: Math.max(0, Math.round(raw.deadheadMiles ?? 0)),
        note: raw.comment || raw.notes || null,
        createdAt: raw.addedAt ?? null,
        deliveredAt: raw.takenAt ?? null,
        weightLb,
        originText: raw.origin ?? null,
        destinationText: raw.destination ?? null,
        brokerName: raw.broker ?? null,
        cutPct: raw.cutPct ?? null,
        paySource: raw.source ?? null,
        keepAfterCutDollars: raw.keepAfterCut == null || !Number.isFinite(Number(raw.keepAfterCut))
            ? null
            : Number(raw.keepAfterCut),
    };
}
/** cutBps from legacy cutPct (percent → basis points). */
export function cutPctToBps(cutPct) {
    return Math.round(cutPct * 100);
}
function asArray(v) {
    return Array.isArray(v) ? v : [];
}
function asRecord(v) {
    return v && typeof v === "object" && !Array.isArray(v)
        ? v
        : {};
}
function numish(v) {
    if (typeof v === "number" && Number.isFinite(v))
        return v;
    if (typeof v === "string" && v.trim()) {
        const n = parseFloat(v.replace(/[$,]/g, ""));
        return Number.isFinite(n) ? n : 0;
    }
    return 0;
}
function placeFromText(text) {
    const t = (text ?? "").trim();
    return {
        text: t,
        city: null,
        state: null,
        zip: null,
        lat: null,
        lng: null,
    };
}
function dedupeKey(raw) {
    const day = (raw.addedAt ?? "").slice(0, 10);
    return [
        String(raw.origin ?? "").trim().toLowerCase(),
        String(raw.destination ?? "").trim().toLowerCase(),
        String(raw.linehaul ?? ""),
        day,
    ].join("|");
}
function fuelPricePaid(r) {
    if (r.price_paid != null && r.price_paid !== "") {
        return numish(r.price_paid);
    }
    return numish(r.pump);
}
function fuelRack(r) {
    if (r.rack_price != null && r.rack_price !== "") {
        return numish(r.rack_price);
    }
    return numish(r.compare);
}
function fuelLocation(r) {
    if (r.location != null && String(r.location).trim()) {
        return String(r.location).trim();
    }
    if (r.note != null && String(r.note).trim())
        return String(r.note).trim();
    return "";
}
/**
 * Compute keepCents the same way the legacy app stored keepAfterCut
 * (lease cut on fee base; fsc/accessorials pulled). Cost ignored.
 */
export function legacyKeepCents(mapped, opts) {
    const cutBps = mapped.paySource === "bennett"
        ? 0
        : cutPctToBps(mapped.cutPct ?? 0);
    const r = analyze({
        linehaulCents: mapped.linehaulCents,
        fscCentsPerMile: mapped.fscCentsPerMile,
        accessorialsCents: mapped.accessorialsCents,
        loadedMiles: mapped.loadedMiles,
        deadheadMiles: mapped.deadheadMiles,
        cutBps,
        kind: PayContractKind.lease_pct,
        fscRule: opts?.fscRule ?? { kind: FscRuleKind.from_load },
        costRule: opts?.costRule ?? { kind: CostRuleKind.per_mile },
        mpg: 6,
        dieselCentsPerGallon: 0,
        otherCentsPerMile: 0,
    });
    return r.keepCents;
}
export function planLegacyImport(bundle, opts = {}) {
    const scope = (opts.scope ?? "").trim();
    const idFor = (kind, key) => legacyEntityId(kind, scope ? `${scope}/${key}` : key);
    const dismissed = asRecord(bundle.haulSheetDismissed);
    const dismissedIds = new Set(Object.keys(dismissed).filter((k) => dismissed[k]));
    const primaryLoads = asArray(bundle.haulSheetLoadLog_v1).filter((r) => r && typeof r === "object");
    const briefLoads = asArray(bundle.prebookBriefLoadLog).filter((r) => r && typeof r === "object");
    const seenBrief = new Set();
    const briefDeduped = [];
    for (const raw of briefLoads) {
        const key = dedupeKey(raw);
        if (seenBrief.has(key))
            continue;
        seenBrief.add(key);
        briefDeduped.push(raw);
    }
    // Prefer primary log ids; add brief-only rows with synthetic ids.
    const primaryIds = new Set(primaryLoads.map((r) => String(r.id ?? "")).filter(Boolean));
    const combined = [...primaryLoads];
    for (const raw of briefDeduped) {
        const id = raw.id ? String(raw.id) : "";
        if (id && primaryIds.has(id))
            continue;
        // Also skip if same lane/day already in primary
        const key = dedupeKey(raw);
        if (primaryLoads.some((p) => dedupeKey(p) === key))
            continue;
        combined.push({
            ...raw,
            id: id || `brief_${dedupeKey(raw)}`,
        });
    }
    let loadsSkippedDismissed = 0;
    const keptLoads = [];
    for (const raw of combined) {
        const id = String(raw.id ?? "");
        if (!id)
            continue;
        if (dismissedIds.has(id)) {
            loadsSkippedDismissed++;
            continue;
        }
        keptLoads.push(raw);
    }
    const countExpensesRaw = bundle.haulSheetCountExpenses;
    const countExpenses = countExpensesRaw === "0" || countExpensesRaw === 0 ? false : true;
    let fscRule = { kind: FscRuleKind.from_load };
    let costRule = { kind: CostRuleKind.per_mile };
    if (!countExpenses) {
        fscRule = { kind: FscRuleKind.fixed_per_mile, centsPerMile: 100 };
        costRule = { kind: CostRuleKind.weekly_truck, weeklyCents: 55_000 };
    }
    const hasBennett = keptLoads.some((r) => String(r.source ?? "").toLowerCase() === "bennett");
    // Cut % for Imported lease: prefer prebookBriefCutPct, else first non-bennett cutPct, else 0
    let leaseCutPct = numish(bundle.prebookBriefCutPct);
    if (!(leaseCutPct > 0)) {
        for (const r of keptLoads) {
            if (String(r.source ?? "").toLowerCase() === "bennett")
                continue;
            if (r.cutPct != null && Number(r.cutPct) > 0) {
                leaseCutPct = Number(r.cutPct);
                break;
            }
        }
    }
    const leaseCutBps = cutPctToBps(leaseCutPct);
    const dates = keptLoads
        .map((r) => r.addedAt || r.takenAt)
        .filter((d) => !!d)
        .sort();
    const earliest = dates[0] ?? null;
    const contracts = [];
    const leaseContractId = idFor("pay_contract", "imported_lease");
    contracts.push({
        id: leaseContractId,
        name: "Imported lease",
        cutBps: leaseCutBps,
        fscRule,
        costRule,
        effectiveFrom: earliest,
    });
    let bennettContractId = null;
    if (hasBennett) {
        bennettContractId = idFor("pay_contract", "bennett");
        contracts.push({
            id: bennettContractId,
            name: "Bennett",
            cutBps: 0,
            fscRule,
            costRule,
            effectiveFrom: earliest,
        });
    }
    const brokerByLower = new Map();
    const loads = [];
    let keepSum = 0;
    let legacyKeepSum = 0;
    let linehaulSum = 0;
    for (const raw of keptLoads) {
        const legacyId = String(raw.id);
        const mapped = mapLegacyLoad(raw, legacyId);
        const id = idFor("load", legacyId);
        let brokerId = null;
        const brokerName = (mapped.brokerName ?? "").trim();
        if (brokerName) {
            const key = brokerName.toLowerCase();
            let broker = brokerByLower.get(key);
            if (!broker) {
                broker = {
                    id: idFor("broker", key),
                    name: brokerName,
                };
                brokerByLower.set(key, broker);
            }
            brokerId = broker.id;
        }
        const isBennett = String(mapped.paySource ?? "").toLowerCase() === "bennett";
        const payContractId = isBennett
            ? bennettContractId
            : leaseContractId;
        const keepCents = legacyKeepCents(mapped, { fscRule, costRule });
        const legacyKeep = mapped.keepAfterCutDollars == null
            ? null
            : toCents(mapped.keepAfterCutDollars);
        keepSum += keepCents;
        if (legacyKeep != null)
            legacyKeepSum += legacyKeep;
        linehaulSum += mapped.linehaulCents;
        loads.push({
            id,
            legacyId,
            status: mapped.status,
            source: "legacy_import",
            brokerId,
            payContractId,
            origin: placeFromText(mapped.originText),
            destination: placeFromText(mapped.destinationText),
            linehaulCents: mapped.linehaulCents,
            fscCentsPerMile: mapped.fscCentsPerMile,
            accessorialsCents: mapped.accessorialsCents,
            loadedMiles: mapped.loadedMiles,
            deadheadMiles: mapped.deadheadMiles,
            weightLb: mapped.weightLb,
            note: mapped.note,
            createdAt: mapped.createdAt,
            deliveredAt: mapped.deliveredAt || null,
            keepCents,
            legacyKeepCents: legacyKeep,
        });
    }
    const expenses = [];
    let documentsQueued = 0;
    for (const raw of asArray(bundle.haulSheetFuelLog)) {
        if (!raw || !raw.id)
            continue;
        const legacyId = String(raw.id);
        const gallons = Math.max(0, numish(raw.gallons));
        const pricePaid = Math.max(0, fuelPricePaid(raw));
        const amountCents = roundCents(pricePaid * gallons * 100);
        const priceMilliCents = roundCents(pricePaid * 100_000);
        const occurredOn = typeof raw.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw.date)
            ? raw.date
            : (raw.at ?? new Date(0).toISOString()).slice(0, 10);
        const hasPhoto = !!(raw.photo && String(raw.photo).startsWith("data:"));
        if (hasPhoto)
            documentsQueued++;
        expenses.push({
            id: idFor("expense", `fuel:${legacyId}`),
            kind: "fuel",
            amountCents,
            priceMilliCents,
            gallons,
            occurredOn,
            note: fuelLocation(raw) || null,
            hasPhoto,
        });
    }
    for (const raw of asArray(bundle.haulSheetMaintLog)) {
        if (!raw || !raw.id)
            continue;
        const legacyId = String(raw.id);
        const costDollars = numish(raw.cost);
        const occurredOn = typeof raw.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw.date)
            ? raw.date
            : (raw.at ?? new Date(0).toISOString()).slice(0, 10);
        const noteParts = [
            raw.item ? String(raw.item) : "",
            raw.mileage != null ? `${raw.mileage} mi` : "",
        ].filter(Boolean);
        expenses.push({
            id: idFor("expense", `maint:${legacyId}`),
            kind: "maintenance",
            amountCents: toCents(costDollars),
            priceMilliCents: null,
            gallons: null,
            occurredOn,
            note: noteParts.join(" · ") || null,
            hasPhoto: false,
        });
    }
    const detentions = [];
    for (const raw of asArray(bundle.haulSheetDetentionLog)) {
        if (!raw || !raw.id || !raw.arrive)
            continue;
        const legacyId = String(raw.id);
        detentions.push({
            id: idFor("detention", legacyId),
            placeText: null,
            arrivedAt: String(raw.arrive),
            departedAt: raw.depart ? String(raw.depart) : null,
            freeHours: Math.max(0, numish(raw.freeHours)),
            rateCentsPerHour: toCents(numish(raw.rate)),
            payCents: raw.pay == null || !Number.isFinite(Number(raw.pay))
                ? null
                : toCents(Number(raw.pay)),
        });
    }
    const runRates = asRecord(bundle.haulSheetRunRates);
    const breakEven = asRecord(bundle.haulSheetBreakEven);
    const costProfile = {};
    const truck = {};
    let hasCost = false;
    let hasTruck = false;
    if (runRates.mpg != null && String(runRates.mpg).trim() !== "") {
        const mpg = numish(runRates.mpg);
        if (mpg > 0) {
            truck.mpg = mpg;
            hasTruck = true;
        }
    }
    if (runRates.dieselGal != null && String(runRates.dieselGal).trim() !== "") {
        const diesel = numish(runRates.dieselGal);
        if (diesel > 0) {
            costProfile.dieselCentsPerGallon = toCents(diesel);
            hasCost = true;
        }
    }
    if (runRates.runCpm != null && String(runRates.runCpm).trim() !== "") {
        // runCpm is $/mi → otherCentsPerMile
        const cpm = numish(runRates.runCpm);
        costProfile.otherCentsPerMile = cpm * 100;
        hasCost = true;
    }
    if (breakEven.fixed != null && String(breakEven.fixed).trim() !== "") {
        costProfile.fixedMonthlyCents = toCents(numish(breakEven.fixed));
        hasCost = true;
    }
    if (breakEven.miles != null && String(breakEven.miles).trim() !== "") {
        costProfile.expectedMilesPerMonth = Math.round(numish(breakEven.miles));
        hasCost = true;
    }
    const detCfgRaw = asRecord(bundle.haulSheetDetentionCfg);
    let detentionCfg = null;
    if (detCfgRaw.rate != null || detCfgRaw.free != null) {
        detentionCfg = {
            rateCentsPerHour: toCents(numish(detCfgRaw.rate)),
            freeHours: Math.max(0, numish(detCfgRaw.free) || 2),
        };
    }
    return {
        counts: {
            loads: loads.length,
            loadsSkippedDismissed,
            brokers: brokerByLower.size,
            fuel: expenses.filter((e) => e.kind === "fuel").length,
            detention: detentions.length,
            maintenance: expenses.filter((e) => e.kind === "maintenance").length,
            contracts: contracts.length,
            documentsQueued,
        },
        totals: {
            keepCents: keepSum,
            legacyKeepCents: legacyKeepSum,
            linehaulCents: linehaulSum,
        },
        brokers: [...brokerByLower.values()],
        loads,
        expenses,
        detentions,
        contracts,
        costProfile: hasCost ? costProfile : null,
        truck: hasTruck ? truck : null,
        detentionCfg,
    };
}
/** Convenience: plan from a raw pasted object (unknown keys ignored). */
export function planLegacyImportFromUnknown(raw, opts = {}) {
    const obj = raw && typeof raw === "object" && !Array.isArray(raw)
        ? raw
        : {};
    return planLegacyImport(obj, opts);
}
//# sourceMappingURL=import.js.map